import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { VStack, Box, Button, CircularProgress, HStack, Heading } from "@chakra-ui/react";

import { userAgentIsMobile, userAgentIsIOS, Browser, getBrowser } from "utils/device";
import StepsBox from "components/StepsBox";
import { fetchProvider } from "utils/backend";
import { extractDomain, extractVariablesFromResponseMatches } from "utils/helper";
import { updateSession } from "utils/update-session";
import { isMobileSafari } from "react-device-detect";
import { Text, Image } from "@chakra-ui/react";
import { THREE_JAN_APP_ID } from "utils/constants";

// Chromium legacy browsers (< Chromium 91.0) do not handle external protocols: https://github.com/blueboxd/chromium-legacy/issues/18

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function usePathname() {
  const { pathname } = useLocation();
  return pathname;
}

const Verifier: React.FC = () => {
  const query = useQuery();
  const pathname = usePathname();
  const APP_ID = process.env.REACT_APP_PUBLIC_APP_ID || "";
  const APP_SECRET = process.env.REACT_APP_PUBLIC_APP_SECRET || "";

  const [appName, setAppName] = useState<string>("");
  const [appImage, setAppImage] = useState<string>("");
  const [appId, setAppId] = useState<string>("");
  const [variables, setVariables] = useState<string[]>([""]);
  const [providerHost, setProviderHost] = useState<string>("");
  const [providerLogo, setProviderLogo] = useState<string>("");
  const [updatedSession, setUpdatedSession] = useState<boolean>(false);
  const [steps, setSteps] = useState<string[]>([]);
  const browser = getBrowser();

  // Open in Safari if not Safari and on iOS
  useEffect(() => {
    if (browser !== Browser.Safari && userAgentIsIOS) {
      window.open(`x-safari-${window.location.href}`, "_blank");
    }
  }, []);


  
  function launchApp(requestUrl: string, deepLink: string) {
    let appInstalled = false;
    let timeoutId: string | number | NodeJS.Timeout | undefined;
    
    // Create hidden iframe to test deep link
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    document.body.appendChild(iframe);
    
    // Function to clean up
    const cleanup = () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    
    // If page becomes hidden, app opened successfully
    const onVisibilityChange = () => {
      if (document.hidden) {
        appInstalled = true;
        cleanup();
        // Open in main window since app is installed
        window.location.href = deepLink;
      }
    };
    
    // Listen for visibility change
    document.addEventListener('visibilitychange', onVisibilityChange, { once: true });
    
    // Test deep link in iframe
    iframe.src = deepLink;
    
    // After timeout, assume app not installed
    timeoutId = setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cleanup();
      
      if (!appInstalled) {
        // App not installed - go directly to instant app URL
        // Use a fresh window context to avoid popup
        window.location.href = requestUrl;
      }
    }, 1500);
  }

  

  const continueOnAppClip = () => {
    const template = query.get("template");
    if (!template) return;
  
    const url = encodeURIComponent(template);
    const encodedUrl = url.replace(/[()]/g, (match) => {
      return "%" + match.charCodeAt(0).toString(16).toUpperCase();
    });
  
    if (userAgentIsIOS) {
      if (pathname.includes("/vana")) {
        window.open(
          `https://share.reclaimprotocol.org/vana-app/?template=${encodedUrl}`,
          "_blank"
        );
        window.location.href = `https://share.reclaimprotocol.org/vana-app/?template=${encodedUrl}`;
      } else {
        window.open(
          `https://appclip.apple.com/id?p=org.reclaimprotocol.app.clip&template=${encodedUrl}`,
          "_blank"
        );
        if (appId.toLowerCase() !== THREE_JAN_APP_ID) {
          window.location.href = `https://share.reclaimprotocol.org/verify/?template=${encodedUrl}`;
        }
      }
    } else {
      // ANDROID flow
      const instantAppUrl =
        appId.toLowerCase() !== THREE_JAN_APP_ID
          ? `https://share.reclaimprotocol.org/verify/?template=${encodedUrl}`
          : `https://share.reclaimprotocol.org/verifier/?template=${encodedUrl}`;
  
      // Construct Android intent deep link
      const deepLink = `intent://details?id=org.reclaimprotocol.app&launch=true&url=${encodeURIComponent(
        instantAppUrl
      )}&template=${encodedUrl}#Intent;scheme=market;action=android.intent.action.VIEW;package=com.android.vending;end;`;
  
      try {
        launchApp(instantAppUrl, deepLink);
      } catch (e) {
        // Final fallback → verifier
        window.location.href = `https://share.reclaimprotocol.org/verifier/?template=${encodedUrl}`;
      }
    }
  };
  

  const nonSafariRedirect = () => {
    const template = query.get("template");
    if (!template) return;
    const url = encodeURIComponent(template);
    const encodedUrl = url.replace(/[()]/g, (match) => {
      return "%" + match.charCodeAt(0).toString(16).toUpperCase();
    });
    if (userAgentIsIOS && !isMobileSafari) {
      window.location.href = `https://share.reclaimprotocol.org/verify/?template=${encodedUrl}`;
    }
  };

  const getAppInfo = async (appId: string) => {
    try {
      const response = await fetch(
        `https://api.reclaimprotocol.org/api/applications/info/${appId}`
      );
      const data = await response.json();
      const appName = data.application.name;
      const appImage = data.application?.appImageUrl ?? null;
      setAppName(appName);
      setAppImage(appImage);
    } catch (error) {
      console.error("Error fetching app name:", error);
    }
  };

  const fetchProviderData = async (providerId: string) => {
    const provider = await fetchProvider(providerId, APP_ID, APP_SECRET);
    const url = provider?.providers.httpProvider[0].loginUrl;
    const providerLogo = provider?.providers.httpProvider[0].logoUrl;
    const domain = extractDomain(url);
    const variables = extractVariablesFromResponseMatches(
      provider?.providers?.httpProvider[0]?.requestData[0]?.responseMatches ?? []
    );
    setVariables(variables);
    setProviderHost(domain);
    setProviderLogo(providerLogo);
    if (provider?.providers.httpProvider[0].stepsToFollow) {
      setSteps(
        provider?.providers.httpProvider[0].stepsToFollow
          .split("\n")
          .map((step: string) => step.trim())
      );
    }
  };

  useEffect(() => {
    try {
      const template = query.get("template");
      const templateObj = template ? JSON.parse(template) : null;
      const applicationId = templateObj?.applicationId;
      const providerId = templateObj?.providerId;
      // nonSafariRedirect();
      getAppInfo(applicationId);
      fetchProviderData(providerId);
      setAppId(applicationId.trim());
    } catch (e) {
      console.error("Error parsing template", e);
    }
  }, []);

  useEffect(() => {
    if (updatedSession) {
      return;
    }
    const template = query.get("template");
    if (!template) return;
    const templateObj = template ? JSON.parse(template) : null;
    const sessionId = templateObj?.sessionId;
    updateSession(sessionId, "USER_INIT_VERIFICATION").then((data) => {
      if (data) {
        setUpdatedSession(true);
      }
    });
  }, [query, updatedSession]);

  return (
    <>
      <VStack mx="auto" spacing={5} justify={userAgentIsMobile ? "space-between" : "center"}>
        {appName !== "" && providerHost !== "" ? (
          <>
            <Box height="10px" />
            {providerLogo && appImage && (
              <Box
                maxWidth="400px"
                width="calc(100% - 24px)"
                mt={5}
                display="flex"
                justifyContent="left"
              >
                <Box
                  w={"62px"}
                  height={"62px"}
                  backgroundImage={appImage}
                  backgroundSize={"cover"}
                  backgroundPosition={"center"}
                  borderRadius={"15px"}
                  border={"5px solid #fff"}
                  zIndex={1}
                />
                <Box
                  w={"61px"}
                  height={"61px"}
                  backgroundImage={providerLogo}
                  backgroundSize={"cover"}
                  backgroundPosition={"center"}
                  borderRadius={"15px"}
                  zIndex={0}
                  marginLeft="-15px"
                />
                {/* <Image
                  src={appImage}
                  alt="App Image"
                  boxSize="41px"
                  borderRadius="12px"
                  border="1px solid white"
                  position="relative"
                  zIndex={1}
                />
                <Image
                  src={providerLogo}
                  alt="Provider Logo"
                  boxSize="41px"
                  borderRadius="12px"
                  border="1px solid white"
                  marginLeft="-10px"
                  position="relative"
                  zIndex={0}
                /> */}
              </Box>
            )}
            <Box
              maxWidth="400px"
              width="calc(100% - 24px)"
              pr={10}
              color="#0F172A"
              lineHeight="27.45px"
              fontSize="23px"
              fontWeight={500}
            >
              <strong>{appName}</strong> is requesting data from{" "}
              <span style={{ textTransform: "capitalize" }}>{providerHost}</span>
            </Box>
            <StepsBox host={providerHost} variables={variables} steps={steps} />
            <VStack
              bg="#F2F2F7"
              px={6}
              pt={4}
              pb={4}
              borderRadius={16}
              maxWidth="400px"
              width="calc(100% - 24px)"
              spacing={2}
              left="50%"
              right="12px"
            >
              <HStack width="100%">
                <Image
                  src="/icons/privacy.svg"
                  alt="steps"
                  objectFit="contain"
                  borderRadius="16px"
                />
                <Heading
                  as="h2"
                  fontSize="18px"
                  lineHeight="19px"
                  fontWeight={500}
                  color={"#1D2126"}
                >
                  Privacy
                </Heading>
              </HStack>

              <Box w={"100%"}>
                <Text
                  color="gray.600"
                  fontSize="16px"
                  lineHeight="19px"
                  fontWeight={400}
                  width="100%"
                  my={2}
                >
                  Your login credentials and private data stay secure and are NEVER visible with{" "}
                  {appName} and Reclaim Protocol.
                </Text>
              </Box>
            </VStack>
          </>
        ) : (
          <CircularProgress color="#007AFF" isIndeterminate pt={10} />
        )}
        {userAgentIsMobile && userAgentIsIOS && browser !== Browser.Safari ? (
          <Button
            maxWidth="400px"
            width="calc(100% - 24px)"
            bg="#007AFF"
            borderRadius="12px"
            height="50px"
            textColor="white"
            lineHeight="22px"
            fontSize="17px"
            fontWeight={400}
            onClick={() => window.open(`x-safari-${window.location.href}`, "_blank")}
            _active={{ bg: "#007AFF" }}
            _hover={{ bg: "#007AFF" }}
          >
            Open in Safari
          </Button>
        ) : (
          <Button
            maxWidth="400px"
            width="calc(100% - 24px)"
            bg="#007AFF"
            borderRadius="12px"
            height="50px"
            textColor="white"
            lineHeight="22px"
            fontSize="17px"
            fontWeight={400}
            onClick={continueOnAppClip}
            _active={{ bg: "#007AFF" }}
            _hover={{ bg: "#007AFF" }}
          >
            {userAgentIsIOS ? "Continue on AppClip" : "Continue on InstantApp"}
          </Button>
        )}
      </VStack>
    </>
  );
};

export default Verifier;
